/**
 * CSEForm.js — standalone Confined Space Evaluation page.
 *
 * Feb-2026 CLL-meeting Phase 4: CSE lives on the Field Reports page
 * (not the Maintenance page anymore). This is a thin standalone
 * wrapper around <ChecklistRenderer> that:
 *
 *   - Loads the "Confined Space Evaluation" template from
 *     /api/checklist-templates
 *   - Renders <JobSelectionCard> at the top (same UX as PreCon and
 *     the equipment checklist forms)
 *   - Submits via /api/maintenance with form_type='Confined Space
 *     Evaluation' — the backend applies the CSE gate (Nate only)
 *
 * Access control: the "New Confined Space" button on FieldReports is
 * hidden unless the current user is in cse_restrictions.submitter_ids,
 * so this route only opens when authorized. The backend enforces the
 * same rule so URL-typing doesn't bypass it.
 */
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { ArrowLeft, Loader2, Save, Send } from 'lucide-react';
import ChecklistRenderer, { flattenItems, initialValuesFor, getMissingRequiredLabels } from '../components/ChecklistRenderer';
import JobSelectionCard from '../components/JobSelectionCard';
import { todayET } from '../lib/dateHelpers';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CSE_TEMPLATE_TITLE = 'Confined Space Evaluation';
const FORM_TYPE = 'Confined Space Evaluation';

export default function CSEForm() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { id: routeFormId } = useParams();  // optional — /confined-space/:id for edit
  // Feb-2026 round 4: /confined-space?job=<id> deep-link from the
  // Job page's "+ Confined Space" button. Auto-selects that job in
  // the JobSelectionCard so Nate doesn't have to pick it manually.
  const [searchParams] = useSearchParams();
  const preselectJobId = searchParams.get('job');
  const isEditing = Boolean(routeFormId);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Template + values
  const [flatItems, setFlatItems] = useState([]);
  const [templateUpdatedAt, setTemplateUpdatedAt] = useState(null);
  const [values, setValues] = useState({});

  // Job selection
  const [availableJobs, setAvailableJobs] = useState([]);
  const [loadingJobs, setLoadingJobs] = useState(true);
  const [selectedJobId, setSelectedJobId] = useState('');
  const selectedJob = availableJobs.find(j => j.id === selectedJobId) || null;

  // Team + assets for dropdown hydration in the template
  const [teamMembers, setTeamMembers] = useState([]);
  const [assetsForSelect, setAssetsForSelect] = useState([]);

  // The current draft (created on first save so photo uploads work)
  const [formDoc, setFormDoc] = useState(null);
  const [uploadingPhotoIdx, setUploadingPhotoIdx] = useState(null);

  // Submitter signature (top-of-form pad — same shape used by other
  // maintenance forms). Simple bool + ref, we send the dataURL on submit.
  const signatureCanvasRef = useRef(null);
  const [hasSignature, setHasSignature] = useState(false);

  // ------------------------------------------------------------------
  // Initial load — template + jobs + team + assets, plus existing
  // form if we're editing.
  // ------------------------------------------------------------------
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const [tpls, jobs, team, assets, existing] = await Promise.all([
          axios.get(`${API}/checklist-templates`).catch(() => ({ data: [] })),
          axios.get(`${API}/jobs/for-field-reports`).catch(() => ({ data: [] })),
          axios.get(`${API}/team`).catch(() => ({ data: [] })),
          axios.get(`${API}/assets`).catch(() => ({ data: [] })),
          isEditing ? axios.get(`${API}/maintenance/${routeFormId}`).catch(() => null) : Promise.resolve(null),
        ]);
        if (!mounted) return;
        const tplList = Array.isArray(tpls.data) ? tpls.data : [];
        const tpl = tplList.find(t => t.title === CSE_TEMPLATE_TITLE);
        if (!tpl) {
          toast.error('CSE template not seeded — contact an admin.');
        } else {
          const flat = flattenItems(tpl.sections || []);
          const blank = initialValuesFor(flat);
          setFlatItems(flat);
          setTemplateUpdatedAt(tpl.updated_at || null);
          if (existing && existing.data) {
            setValues({ ...blank, ...(existing.data.checklist_values || {}) });
            setFormDoc(existing.data);
            setSelectedJobId(existing.data.job_id || '');
          } else {
            setValues(blank);
            // ?job=<id> pre-select (deep link from Job page).
            if (preselectJobId) setSelectedJobId(preselectJobId);
          }
        }
        setAvailableJobs(Array.isArray(jobs.data) ? jobs.data : []);
        setTeamMembers(Array.isArray(team.data) ? team.data : []);
        setAssetsForSelect((Array.isArray(assets.data) ? assets.data : []).filter(a => a && a.name));
      } catch (e) {
        console.error('CSEForm load failed', e);
        toast.error('Failed to load Confined Space form.');
      } finally {
        if (mounted) {
          setLoading(false);
          setLoadingJobs(false);
        }
      }
    })();
    return () => { mounted = false; };
  }, [isEditing, routeFormId, preselectJobId]);

  // ------------------------------------------------------------------
  // Signature pad (mirrors Maintenance.js — kept minimal here)
  // ------------------------------------------------------------------
  const startDrawing = (e) => {
    const canvas = signatureCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = ((e.touches ? e.touches[0].clientX : e.clientX) - rect.left) * (canvas.width / rect.width);
    const y = ((e.touches ? e.touches[0].clientY : e.clientY) - rect.top) * (canvas.height / rect.height);
    ctx.beginPath();
    ctx.moveTo(x, y);
    canvas._drawing = true;
  };
  const draw = (e) => {
    const canvas = signatureCanvasRef.current;
    if (!canvas || !canvas._drawing) return;
    e.preventDefault();
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = ((e.touches ? e.touches[0].clientX : e.clientX) - rect.left) * (canvas.width / rect.width);
    const y = ((e.touches ? e.touches[0].clientY : e.clientY) - rect.top) * (canvas.height / rect.height);
    ctx.lineTo(x, y);
    ctx.strokeStyle = '#0f172a';
    ctx.lineWidth = 2;
    ctx.stroke();
    setHasSignature(true);
  };
  const stopDrawing = () => {
    const canvas = signatureCanvasRef.current;
    if (canvas) canvas._drawing = false;
  };
  const clearSignature = () => {
    const canvas = signatureCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
  };

  const handleChecklistChange = useCallback((idx, key, value) => {
    setValues(prev => ({ ...prev, [idx]: { ...(prev[idx] || {}), [key]: value } }));
  }, []);

  const jobPayload = () => (selectedJob ? {
    job_id: selectedJob.id,
    job_number: selectedJob.job_number,
    job_service_address: selectedJob.service_address || '',
    job_date: selectedJob.date || '',
    job_assigned_team: selectedJob.assigned_team_names || [],
  } : {});

  // ------------------------------------------------------------------
  // Save draft / Submit
  // ------------------------------------------------------------------
  const ensureDraft = async () => {
    if (formDoc?.id) return formDoc;
    const payload = {
      form_type: FORM_TYPE,
      equipment_name: 'N/A (safety permit)',
      equipment_id: '',
      date: todayET(),
      performed_by: user?.full_name || '',
      checklist: [],
      checklist_values: values,
      checklist_template_title: CSE_TEMPLATE_TITLE,
      notes: '',
      mileage: null,
      condition: '',
      ...jobPayload(),
    };
    const res = await axios.post(`${API}/maintenance`, payload);
    setFormDoc(res.data);
    return res.data;
  };

  const handleSaveDraft = async () => {
    setSaving(true);
    try {
      const doc = await ensureDraft();
      if (doc?.id) {
        await axios.put(`${API}/maintenance/${doc.id}`, {
          form_type: FORM_TYPE,
          equipment_name: doc.equipment_name || 'N/A (safety permit)',
          equipment_id: doc.equipment_id || '',
          date: doc.date || todayET(),
          performed_by: user?.full_name || '',
          checklist: [],
          checklist_values: values,
          checklist_template_title: CSE_TEMPLATE_TITLE,
          notes: doc.notes || '',
          ...jobPayload(),
        });
      }
      toast.success('Draft saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save draft');
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async () => {
    if (!selectedJobId) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      toast.error('Please select a job first.');
      return;
    }
    const missing = getMissingRequiredLabels(flatItems, values);
    if (missing.length > 0) {
      // Scroll to top — Submit sits at the bottom of a long checklist,
      // so this warning would otherwise go unseen (2026-09-09 field report).
      window.scrollTo({ top: 0, behavior: 'smooth' });
      toast.error(
        `Please complete ${missing.length} required field${missing.length > 1 ? 's' : ''} before submitting: ${missing.slice(0, 3).join('; ')}${missing.length > 3 ? '…' : ''}`,
        { duration: 8000 }
      );
      return;
    }
    if (!hasSignature) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      toast.error('Please add your signature before submitting.');
      return;
    }
    setSubmitting(true);
    try {
      const doc = await ensureDraft();
      // Persist current values first
      await axios.put(`${API}/maintenance/${doc.id}`, {
        form_type: FORM_TYPE,
        equipment_name: doc.equipment_name || 'N/A (safety permit)',
        equipment_id: doc.equipment_id || '',
        date: doc.date || todayET(),
        performed_by: user?.full_name || '',
        checklist: [],
        checklist_values: values,
        checklist_template_title: CSE_TEMPLATE_TITLE,
        notes: doc.notes || '',
        ...jobPayload(),
      });
      // Submit for approval — signature required.
      const dataUrl = signatureCanvasRef.current.toDataURL('image/png');
      await axios.post(`${API}/maintenance/${doc.id}/submit`, {
        signature: dataUrl,
      });
      toast.success('Confined Space Evaluation submitted for approval');
      navigate('/field-reports');
    } catch (err) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      toast.error(err.response?.data?.detail || 'Failed to submit', { duration: 8000 });
    } finally {
      setSubmitting(false);
    }
  };

  // ------------------------------------------------------------------
  // Photo upload — routes to the existing /maintenance photo endpoint.
  // ------------------------------------------------------------------
  const handlePhotoUpload = async (idx, file) => {
    try {
      const doc = await ensureDraft();
      setUploadingPhotoIdx(idx);
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(
        `${API}/maintenance/${doc.id}/checklist/${idx}/photo`,
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      const url = res.data?.url;
      if (url) {
        setValues(prev => ({
          ...prev,
          [idx]: { ...(prev[idx] || {}), photos: [...((prev[idx] || {}).photos || []), { url }] },
        }));
      }
    } catch (err) {
      toast.error('Photo upload failed');
    } finally {
      setUploadingPhotoIdx(null);
    }
  };

  if (loading) {
    return (
      <div className="p-8 flex justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-amber-500" />
      </div>
    );
  }

  return (
    <div className="p-4 space-y-6 max-w-2xl mx-auto pb-24">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/field-reports')}
            data-testid="cse-back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <h1 className="text-2xl font-['Barlow_Condensed'] font-bold">
            Confined Space Evaluation
          </h1>
        </div>
        {templateUpdatedAt && (
          <span className="text-xs text-slate-400" data-testid="cse-checklist-updated-at">
            Updated: {new Date(templateUpdatedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
          </span>
        )}
      </div>

      <JobSelectionCard
        value={selectedJobId}
        onChange={setSelectedJobId}
        availableJobs={availableJobs}
        loadingJobs={loadingJobs}
        selectedJob={selectedJob}
        testIdPrefix="cse-job-selection"
      />

      {flatItems.length === 0 ? (
        <Card>
          <CardContent className="p-4 text-sm text-slate-500 italic">
            CSE template not available — ask an admin to seed the &quot;{CSE_TEMPLATE_TITLE}&quot; checklist template.
          </CardContent>
        </Card>
      ) : (
        <ChecklistRenderer
          flatItems={flatItems}
          values={values}
          onChange={handleChecklistChange}
          onPhotoUpload={handlePhotoUpload}
          uploadingPhotoIdx={uploadingPhotoIdx}
          teamMembers={teamMembers}
          assetsForSelect={assetsForSelect}
          testIdPrefix="cse"
        />
      )}

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg font-['Barlow_Condensed']">Signature</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <Label className="font-medium text-sm">Sign below to submit for approval</Label>
          <div className="border-2 border-dashed border-slate-300 rounded-lg p-2 bg-white">
            <canvas
              ref={signatureCanvasRef}
              width={400}
              height={150}
              className="w-full border border-slate-200 rounded cursor-crosshair touch-none"
              onMouseDown={startDrawing}
              onMouseMove={draw}
              onMouseUp={stopDrawing}
              onMouseLeave={stopDrawing}
              onTouchStart={startDrawing}
              onTouchMove={draw}
              onTouchEnd={stopDrawing}
              data-testid="cse-signature-canvas"
            />
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={clearSignature}
              className="mt-2 text-slate-500"
              data-testid="cse-signature-clear"
            >
              Clear Signature
            </Button>
          </div>
        </CardContent>
      </Card>

      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 p-3 flex justify-end gap-2 z-40">
        <Button
          variant="outline"
          onClick={handleSaveDraft}
          disabled={saving || submitting}
          data-testid="cse-save-draft-btn"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
          Save Draft
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={submitting || saving || !selectedJobId || !hasSignature}
          className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold"
          data-testid="cse-submit-btn"
        >
          {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
          Submit for Approval
        </Button>
      </div>
    </div>
  );
}
